/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { ChangeEvent, memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormControl, FormHelperText, InputLabel, OutlinedInput } from '@mui/material';

import { CountryCode, CountryCodes, KubernetesIdentifier } from 'shared';

import { useEventCallback } from '../../../../../components/hooks/useEventCallback';
import { ArrowRoundedIcon } from '../../../../../components/svg-icons/ArrowRoundedIcon';
import {
  ThumbnailSelector,
  ThumbnailSelectorType,
} from '../../../../../components/ThumbnailSelector/ThumbnailSelector';
import {
  CreateOrganizationApiValidationErrors,
  CreateOrganizationValues,
  ResetApiErrorForField,
} from '../../../../../containers/CreateOrganizationOverlay/CreateOrganizationOverlay.types';
import { HUAutocomplete } from '../../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../../i18n';
import { useFormStyles } from '../../../../../styles/form';
import { ThumbnailOption } from '../../../../../types/thumbnail/ThumbnailOption';
import { readFile } from '../../../../../utils/canvasUtils';
import { useSetupPageStyles } from '../../../SetupPage.styles';

type OrganizationFormProps = {
  resetApiError: ResetApiErrorForField;
  apiErrors: CreateOrganizationApiValidationErrors;
  isFetching: boolean;
  onSkip: () => void;
} & FormikProps<CreateOrganizationValues>;

export const OrganizationForm = memo<OrganizationFormProps>(props => {
  const {
    handleSubmit,
    values,
    setFieldValue,
    setValues,
    handleChange,
    handleBlur,
    errors,
    touched,
    apiErrors,
    resetApiError,
    isFetching,
    onSkip,
  } = props;
  const { t, i18n } = useTranslation([Namespaces.CreateOrganization, Namespaces.Common]);

  const classes = useSetupPageStyles();
  const formClasses = useFormStyles();

  const { thumbnailsOptions } = values;

  const addressValues = values.address || {};
  const addressTouched = touched.address || {};
  const addressErrors = errors.address || {};

  useEffect(() => {
    void setFieldValue('companyName', values.name);
  }, [setFieldValue, values.name]);

  useEffect(() => {
    void setFieldValue('language', i18n.language);
  }, [i18n.language, setFieldValue]);

  useEffect(() => {
    if (values.thumbnail === null && thumbnailsOptions[0]) {
      void setFieldValue('thumbnail', thumbnailsOptions[0]);
    }
  }, [setFieldValue, thumbnailsOptions, values.thumbnail]);

  const onImageUpload = useEventCallback(async (thumbnail: Blob) => {
    const newThumbnailOption: ThumbnailOption = { type: 'base64', base64: await readFile(thumbnail) };
    void setValues({
      ...values,
      thumbnailsOptions: [...thumbnailsOptions, newThumbnailOption],
      thumbnail: newThumbnailOption,
    });
  });

  const onChange = (name: keyof CreateOrganizationApiValidationErrors) => (event: ChangeEvent) => {
    handleChange(event);
    resetApiError(name);
  };

  const handleCountryChange = useCallback(
    async (_e: unknown, value: CountryCode | null) => {
      if (value) {
        await setFieldValue('address.countryCode', value, true);
        resetApiError('address.countryCode');
      }
    },
    [resetApiError, setFieldValue],
  );

  const getOptionLabel = useCallback((value: CountryCode) => t(`countries:${value}.name`), [t]);

  const nameError = errors.name || apiErrors.name;
  const hasNameError = touched.name && !!nameError;

  const line1Error = addressErrors.line1 || apiErrors['address.line1'];
  const hasLine1Error = addressTouched.line1 && !!line1Error;

  const postalCodeError = addressErrors.postalCode || apiErrors['address.postalCode'];
  const hasPostalCodeError = addressTouched.postalCode && !!postalCodeError;

  const cityError = addressErrors.city || apiErrors['address.city'];
  const hasCityError = addressTouched.city && !!cityError;

  const countryCodeError = addressErrors.countryCode || apiErrors['address.countryCode'];
  const hascountryCodeError = addressTouched.countryCode && !!countryCodeError;

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      {!!thumbnailsOptions.length && (
        <FormControl className={cx(formClasses.formControl, formClasses.widthFull)}>
          <ThumbnailSelector
            onItemClick={value => setFieldValue('thumbnail', value)}
            options={thumbnailsOptions}
            type={ThumbnailSelectorType.organization}
            value={values.thumbnail}
            allowUploadImage={true}
            onImageUpload={onImageUpload}
          />
        </FormControl>
      )}
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull)}
        error={hasNameError}
        variant="outlined"
      >
        <InputLabel htmlFor="name">{t(`${Namespaces.Setup}:page.organization.fields.organization_name`)}</InputLabel>
        <OutlinedInput
          id="name"
          name="name"
          value={values.name}
          onChange={onChange('name')}
          label={t(`${Namespaces.Setup}:page.organization.fields.organization_name`)}
          onBlur={handleBlur}
          data-cy="create-organization-form-name-input"
        />
        {hasNameError && (
          <FormHelperText data-cy="create-organization-form-name-error">
            {t(`validation.name.${nameError}`, {
              minLength: KubernetesIdentifier.MinLength,
              maxLength: KubernetesIdentifier.MaxLength,
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull)}
        error={hasLine1Error}
        variant="outlined"
      >
        <InputLabel htmlFor="address.line1">
          {t(`${Namespaces.Setup}:page.organization.fields.address_line_1`)}
        </InputLabel>
        <OutlinedInput
          id="address.line1"
          name="address.line1"
          value={addressValues.line1}
          onChange={onChange('address.line1')}
          label={t(`${Namespaces.Setup}:page.organization.fields.address_line_1`)}
          onBlur={handleBlur}
          data-cy="create-organization-form-address-line-1-input"
        />
        {hasLine1Error && (
          <FormHelperText data-cy="create-organization-form-address-line-1-error">
            {t(`validation.address.line1.${line1Error}`)}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull)}
        error={hasPostalCodeError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.postalCode">{t('input_labels.postal_code')}</InputLabel>
        <OutlinedInput
          id="address.postalCode"
          name="address.postalCode"
          value={addressValues.postalCode}
          onChange={onChange('address.postalCode')}
          label={t('input_labels.postal_code')}
          onBlur={handleBlur}
          data-cy="create-organization-form-postal-code-input"
        />
        {hasPostalCodeError && (
          <FormHelperText data-cy="create-organization-form-postal-code-error">
            {t(`validation.address.postalCode.${postalCodeError}`)}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull)}
        error={hasCityError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.city">{t('input_labels.city')}</InputLabel>
        <OutlinedInput
          id="address.city"
          name="address.city"
          value={addressValues.city}
          onChange={onChange('address.city')}
          label={t('input_labels.city')}
          onBlur={handleBlur}
          data-cy="create-organization-form-city-input"
        />
        {hasCityError && (
          <FormHelperText data-cy="create-organization-form-city-error">
            {t(`validation.address.city.${cityError}`)}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull)}
        error={hascountryCodeError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.countryCode">{t('input_labels.country_code')}</InputLabel>
        <HUAutocomplete
          popupIcon={<ArrowRoundedIcon />}
          renderInput={renderParams => (
            <OutlinedInput
              {...destructRenderInputParams(renderParams)}
              name="address.countryCode"
              label={t('input_labels.country_code')}
            />
          )}
          options={[...CountryCodes]}
          disableClearable={true}
          value={addressValues.countryCode}
          onChange={handleCountryChange}
          getOptionLabel={getOptionLabel}
        />
        {hascountryCodeError && (
          <FormHelperText>{t(`validation.address.countryCode.${countryCodeError}`)}</FormHelperText>
        )}
      </FormControl>
      <div className={classes.formActions}>
        <Button fullWidth size="small" variant="outlined" disabled={isFetching} onClick={onSkip}>
          {t(`${Namespaces.Setup}:page.buttons.skip`)}
        </Button>
        <Button
          fullWidth
          size="small"
          variant="contained"
          type="submit"
          disabled={isFetching}
          data-cy="setup-organization-form-submit"
        >
          {t(`${Namespaces.Setup}:page.buttons.submit`)}
        </Button>
      </div>
    </form>
  );
});
